// pages/module-pages/sites/SitesPage.tsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff, Edit, Trash2 } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import PageListLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import SitesList from "../../../components/shared/sites/SitesList";
import { Site } from "../../../types/features/Site";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import useSites from "../../../hooks/features/sites/useSites";

export default function SitesPage() {
  const navigate = useNavigate();
  const { sites, loading, deleteSite, toggleActive } = useSites();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filtrer les sites par recherche
  const filteredSites = useMemo(() => {
    let result = sites;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(searchLower) ||
          s.slug.toLowerCase().includes(searchLower) ||
          (s.domain && s.domain.toLowerCase().includes(searchLower))
      );
    }

    return result;
  }, [sites, searchTerm]);

  // Stats
  const stats = {
    total: sites.length,
    active: sites.filter((s) => s.is_active).length,
    inactive: sites.filter((s) => !s.is_active).length,
  };

  const handleToggleActive = async (site: Site) => {
    const success = await toggleActive(site.id, site.is_active);
    if (success) {
      alertSuccess(site.is_active ? "Site désactivé" : "Site activé");
    } else {
      alertError("Erreur lors du changement de statut");
    }
  };

  const handleDelete = async () => {
    if (!siteToDelete) return;
    setIsDeleting(true);
    const success = await deleteSite(siteToDelete.id);
    setIsDeleting(false);
    if (success) {
      setSiteToDelete(null);
      alertSuccess("Site supprimé avec succès");
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    // Pour l'instant, on supprime un par un
    let allSuccess = true;
    for (const id of itemsToDelete) {
      const success = await deleteSite(id);
      if (!success) allSuccess = false;
    }
    if (allSuccess) {
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} sites supprimés`);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const getActionsMenu = (site: Site): Menu[] => {
    return [
      {
        label: site.is_active ? "Désactiver" : "Activer",
        icon: site.is_active ? EyeOff : Eye,
        onClick: () => handleToggleActive(site),
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => navigate(`/admin/sites/update?id=${site.id}`),
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => setSiteToDelete(site),
      },
    ];
  };

  return (
    <PageListLayout title="Gestion des sites">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
              <p className="text-sm text-gray-500 mt-1">
                Gérez tous les sites vitrines de KAFINTECH Group
              </p>
            </div>
            <button
              onClick={() => navigate("/admin/sites/new")}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Plus size={18} />
              Nouveau site
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Actifs</p>
              <p className="text-2xl font-bold text-green-700">{stats.active}</p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Inactifs</p>
              <p className="text-2xl font-bold text-gray-700">{stats.inactive}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        <SitesList
          sites={filteredSites}
          onAction={(site) => setSelectedSite(site)}
          onSelectSites={(selected) => setItemsToDelete(selected.map((s) => s.id))}
          selectable={true}
          loading={loading}
          selectActions={[
            {
              label: "Supprimer",
              onClick: (selected) => setItemsToDelete(selected.map((s) => s.id)),
              className: "bg-red-600 text-white",
            },
          ]}
        />
      </div>

      {/* Menu modal */}
      {selectedSite && (
        <MenuModal
          title={selectedSite.name}
          isOpen={!!selectedSite}
          onClose={() => setSelectedSite(null)}
          icon={null}
          menu={getActionsMenu(selectedSite)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!siteToDelete}
        onClose={() => setSiteToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le site"
        message={`Supprimer le site "${siteToDelete?.name}" ? Toutes les pages associées seront également supprimées.`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} site(s) ?`}
      />
    </PageListLayout>
  );
}